import { z } from "zod";
import { verifySocialLinks } from "@/lib/social-links/verify-social-links";
import { localizedTextSchema } from "./localized-text";
import { menuNodeSchema, type FOOTER_WIDTHS } from "./site-menu";
import { verifyMenuShape } from "./verify-site-layout";

/** Where the brand block of the footer sits: before the link columns, above them, or after them. */
export const FOOTER_BRAND_POSITIONS = ["start", "top", "end"] as const;

/** How the footer is laid out: how many link columns sit in a row on a wide screen (2 to 6) and where the brand block goes. A wrong saved value becomes the default instead of refusing the whole footer. */
const footerLayoutSchema = z.object({
  columns: z.number().int().min(2).max(6).catch(3),
  brand: z.enum(FOOTER_BRAND_POSITIONS).catch("start"),
});

/** A footer saved before the layout existed shows all its columns in one row; so it gets that many (2 to 6) as its column count and the look does not change. Anything else is left for the schema to judge. */
export function migrateSiteFooter(raw: unknown): unknown {
  if (typeof raw !== "object" || raw === null || "layout" in raw) return raw;
  const count = "columns" in raw && Array.isArray(raw.columns) ? raw.columns.length : 0;
  return { ...raw, layout: { columns: Math.min(6, Math.max(2, count)), brand: "start" } };
}

const contact = {
  email: z.string().max(200).refine((v) => v === "" || z.email().safeParse(v).success),
  phone: z.string().max(30).regex(/^[+0-9 ()-]*$/),
  address: z.string().max(500),
};

/** The foot of the public site: link columns, social links (the same lines `network address` as the newsletter's), the brand block and the bottom bar. */
export const siteFooterSchema = z.preprocess(migrateSiteFooter, z.object({
  columns: z.array(menuNodeSchema).max(6),
  layout: footerLayoutSchema,
  social: z.string().refine((v) => verifySocialLinks(v).ok),
  brand: z.object({ logo: z.boolean(), wordmark: z.boolean(), description: localizedTextSchema, ...contact }),
  bottom: z.object({ copyright: localizedTextSchema, legal: z.array(menuNodeSchema).max(6), languageSwitcher: z.boolean(), backToTop: z.boolean() }),
}).refine((footer) => verifyMenuShape([...footer.columns, ...footer.bottom.legal]).ok));

export type SiteFooter = z.infer<typeof siteFooterSchema>;

const WEIGHTS = {
  narrow: "[--w:1] md:[--w:0.7]",
  normal: "[--w:1]",
  wide: "[--w:1] md:[--w:1.5]",
} as const;

/** The classes of one footer link column: a share of the row (`--w`, narrow 0.7, normal 1, wide 1.5 from a tablet up) that grows to fill the row and wraps when the row is full. */
export function footerColumnClass(width: (typeof FOOTER_WIDTHS)[number] | undefined): string {
  return `min-w-0 grow-(--w) basis-[calc((100%_-_(var(--cols)_-_1)*2.5rem)/var(--cols)*var(--w)_-_0.1px)] ${WEIGHTS[width ?? "normal"]}`;
}

const DESKTOP = { 2: "lg:[--cols:2]", 3: "lg:[--cols:3]", 4: "lg:[--cols:4]", 5: "lg:[--cols:5]", 6: "lg:[--cols:6]" } as const;
const TABLET = { 2: "md:[--cols:2]", 3: "md:[--cols:3]", 4: "md:[--cols:3]", 5: "md:[--cols:3]", 6: "md:[--cols:3]" } as const;

/** The classes that lay the footer out: `outer` (brand block and links), `brand` and `links`. Columns beyond the count wrap onto a new row, never hide; two per row on a phone, up to three on a tablet. */
export function footerLayoutClasses({ columns, brand }: SiteFooter["layout"]) {
  const count = columns as keyof typeof DESKTOP;
  return {
    outer: brand === "top" ? "flex flex-col gap-10" : "flex flex-col gap-10 lg:flex-row",
    brand: brand === "end" ? "order-last lg:w-1/3 lg:shrink-0" : brand === "top" ? "" : "lg:w-1/3 lg:shrink-0",
    links: `flex min-w-0 flex-1 flex-wrap gap-x-10 gap-y-10 [--cols:2] ${TABLET[count]} ${DESKTOP[count]}`,
  };
}
